import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Empty, Input, Space, Tag, Typography } from "antd";
import { SendOutlined } from "@ant-design/icons";

const { Text } = Typography;

const MAX_MESSAGE_CHARS = 2000;

function formatTime(value) {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleTimeString();
}

function originColor(origin) {
    return String(origin) === "operator" ? "blue" : "purple";
}

export default function TerminalChatPanel({
    messages = [],
    onSend,
    connected = false,
    chatEnabled = true,
    displayName = "",
    height = 320,
}) {
    const [draft, setDraft] = useState("");
    const scrollRef = useRef(null);

    const canSend = useMemo(
        () => Boolean(connected && chatEnabled && draft.trim() && typeof onSend === "function"),
        [connected, chatEnabled, draft, onSend],
    );

    useEffect(() => {
        const node = scrollRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [messages]);

    const submit = useCallback(() => {
        const text = draft.trim();
        if (!text || !connected || !chatEnabled || typeof onSend !== "function") return;
        onSend(text.slice(0, MAX_MESSAGE_CHARS));
        setDraft("");
    }, [chatEnabled, connected, draft, onSend]);

    return (
        <Card
            size="small"
            title="Session Chat"
            extra={
                <Space size={8}>
                    {displayName ? <Tag color="default">You: {displayName}</Tag> : null}
                    <Badge
                        status={connected && chatEnabled ? "processing" : "default"}
                        text={connected && chatEnabled ? "Live" : "Offline"}
                    />
                </Space>
            }
        >
            {!chatEnabled ? (
                <Alert
                    type="info"
                    showIcon
                    message="Session chat is disabled by tenant policy."
                    style={{ marginBottom: 12 }}
                />
            ) : null}

            <div
                ref={scrollRef}
                style={{
                    height,
                    overflowY: "auto",
                    padding: 8,
                    borderRadius: 8,
                    border: "1px solid #1e293b",
                    background: "#0b1220",
                    marginBottom: 12,
                }}
            >
                {messages.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<Text style={{ color: "#94a3b8" }}>No messages yet</Text>}
                    />
                ) : (
                    messages.map((entry, index) => (
                        <div key={`${entry.at || ""}-${index}`} style={{ marginBottom: 10 }}>
                            <Space size={6} wrap>
                                <Tag color={originColor(entry.origin)} style={{ marginInlineEnd: 0 }}>
                                    {String(entry.sender || "unknown")}
                                </Tag>
                                <Text style={{ color: "#64748b", fontSize: 11 }}>{formatTime(entry.at)}</Text>
                            </Space>
                            <div
                                style={{
                                    color: "#e2e8f0",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    marginTop: 2,
                                }}
                            >
                                {String(entry.message || "")}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Space.Compact style={{ width: "100%" }}>
                <Input.TextArea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={chatEnabled ? "Message the other session participants" : "Chat disabled"}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    maxLength={MAX_MESSAGE_CHARS}
                    disabled={!connected || !chatEnabled}
                    onPressEnter={(event) => {
                        if (event.shiftKey) return;
                        event.preventDefault();
                        submit();
                    }}
                />
                <Button type="primary" icon={<SendOutlined />} onClick={submit} disabled={!canSend}>
                    Send
                </Button>
            </Space.Compact>
        </Card>
    );
}
